import { useState } from "react";
import { Button, Icon, Loading, Modal, MultiEmailInput, Typography } from "@/components/atoms";
import { useRequestAccessMutation } from "@/redux/apis/assistants/assistantsApi";
import { RequestAccessError } from "@/redux/apis/assistants/types";
import { addToast } from "@/utils";

/** Pull the per-email failures out of a 422 ApiProblem body (all requests failed). */
const getRequestErrors = (error: unknown): RequestAccessError[] => {
  const data = (error as { data?: { errors?: RequestAccessError[] } })?.data;
  return Array.isArray(data?.errors) ? data!.errors : [];
};

interface RequestAccessModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * EA "Request Client Access" modal — multi-email chips with the batch
 * {success_count,total_count,errors} handling, pointed at `/access-request`.
 *
 * The Figma shows a live "N existing / N will be invited" split, but there's no pre-send lookup
 * endpoint and the batch response doesn't return per-email type, so we show a static informational
 * line and surface a post-send summary via toasts. (Follow-up: a lookup endpoint or per-email
 * response type would let us render the live split.)
 */
export const RequestAccessModal = ({ open, onClose }: RequestAccessModalProps) => {
  const [emails, setEmails] = useState<string[]>([]);
  const [requestAccess, { isLoading }] = useRequestAccessMutation();

  const handleClose = () => {
    setEmails([]);
    onClose();
  };

  const handleSend = async () => {
    if (emails.length === 0) {
      addToast("info", "Please enter at least one email address");
      return;
    }

    try {
      const { success_count, errors = [] } = await requestAccess({ emails }).unwrap();

      addToast("success", `Access requested for ${success_count} client(s)`);
      // Partial success: some emails were skipped (e.g. already connected or invalid).
      errors.forEach(({ email, error }) => addToast("danger", `${email}: ${error}`));

      handleClose();
    } catch (error) {
      // All requests failed (422) — surface the specific reason per email.
      const requestErrors = getRequestErrors(error);
      if (requestErrors.length) {
        requestErrors.forEach(({ email, error: message }) => addToast("danger", `${email}: ${message}`));
      } else {
        addToast("danger", "Failed to send requests. Please try again.");
      }
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      variant="web-medium"
      title="Request access to a client's account"
      description="Send a request to manage your client's bookings, preferences, and account details. They can approve it by logging in to their account. If you manage their inbox, you can approve it on their behalf."
      renderButtons={() => (
        <>
          <Button variant="secondary" size="lg" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" size="lg" onClick={handleSend} disabled={emails.length === 0 || isLoading}>
            Send Requests
          </Button>
        </>
      )}
    >
      {isLoading && <Loading />}
      <div className="flex flex-col gap-y-4">
        <div className="flex flex-col gap-y-1">
          <Typography variant="action" className="text-neutral-dark-gray">
            Client&apos;s Email
          </Typography>
          <MultiEmailInput emails={emails} onChange={setEmails} />
          <Typography variant="paragraph" className="text-neutral-silver-gray">
            You can enter multiple email addresses separated by commas
          </Typography>
        </div>

        {/* Informational box — the live existing-vs-new split isn't available pre-send (no lookup
            endpoint), so we explain the flow and report the outcome via toasts after sending. */}
        <div className="flex items-start gap-2 rounded-lg bg-neutral-surface-gray p-3">
          <Icon name="InfoCircle" size="sm" className="mt-0.5 shrink-0 text-neutral-dark-gray" />
          <Typography variant="paragraph" className="text-neutral-dark-gray">
            Existing users will get an access request to approve. New users create their account first, then approve your access.
            You&apos;ll be notified when each client responds.
          </Typography>
        </div>
      </div>
    </Modal>
  );
};
